import { Button, Spacer, Stack } from '@chakra-ui/react'
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons'
import React, { useCallback } from 'react'

import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType } from '@/components/UI/Form/types'
import Icon from '@/components/UI/Icon/Icon'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function CompleteTaskModal({
  title,
  body,
  link,
  ctaLabel = 'Start Task',
  open,
  onClose,
  onMarkComplete,
}: {
  title: string
  body?: () => JSX.Element
  link: string
  ctaLabel?: string
  open: boolean
  onClose: () => void
  onMarkComplete: () => Promise<void>
}): JSX.Element {
  const toast = useToaster()
  const markCompleteFormInputs: FormInputType[] = React.useMemo(
    () => [
      {
        type: 'multi-checkbox',
        name: 'mark_complete',
        label: 'Done with this task?',
        sublabel: 'You will no longer be able to access the link once you mark it as completed.',
        options: [
          {
            value: 'certifyCompleted',
            label: 'I certify that I have completed this task.',
          },
        ],
        required: true,
        size: 6,
      },
    ],
    []
  )

  const handleMarkTaskCompleted = useCallback(async () => {
    toast({
      title: 'Marking task as complete...',
      status: 'info',
    })
    await onMarkComplete()
    onClose()

    toast({
      title: 'Successfully marked task as completed',
      status: 'success',
    })
  }, [onClose, onMarkComplete, toast])

  return (
    <ModalV3
      size="xl"
      open={open}
      onClose={onClose}
      title={title}
      body={() => (
        <Stack direction="column" spacing="24px">
          {body != null ? body() : null}
          <ShwLink href={link}>
            <Button width="100%" rightIcon={<Icon faIcon={faExternalLink} />}>
              {ctaLabel}
            </Button>
          </ShwLink>
          <Form
            name="MarkTaskCompleted"
            blocks={markCompleteFormInputs}
            onSubmit={handleMarkTaskCompleted}
            FooterComponent={({ submitting }) => (
              <Stack direction="row" spacing="8px" mt="16px">
                <Spacer />
                <Button variant="outline" onClick={onClose}>
                  Not now
                </Button>
                <Button isLoading={submitting} type="submit">
                  Mark Complete
                </Button>
              </Stack>
            )}
          />
        </Stack>
      )}
    />
  )
}
